import { Controller } from "@hotwired/stimulus"
import { getJSON, sendJSON, errorDetail, formatPln, listResults } from "../api.js"

// lead-list (root = <main id="ll-main">) — catalog row list fed from DRF /api/catalog/.
//
// Responsibilities:
//   - load rows on connect (forward all URL filter params + f_* keys + vertical)
//   - render each row from <template id="tpl-ll-row"> using textContent + cloneNode (no innerHTML)
//   - views: "compact" | "extended" (localStorage["ll-view"], default extended)
//     compact only ≥1181 px (matchMedia); compact hides the facts line, row click = select,
//     the eye button (.ll-peek) toggles the row's accordion (drawer + variant card) like „Więcej"
//   - row interactions: click → accordion (extended) or select (compact)
//   - cart: add exclusive (default) / slot (caret menu or accordion radio);
//     after add/remove/bulk add: update row state, emit lead-list:cart, refresh cart controller count,
//     then re-GET the row (retrieve endpoint) to refresh its slot data in place
//   - selection + bulk bar C26: check-all (indeterminate), bulk variant, bulk add
//
// Zero PII: the catalog serializer is 0-PII; every slot is filled with textContent.
// No raw fetch: all HTTP goes through getJSON / sendJSON from api.js.

const FILTER_KEYS = [
  "subcategory", "category", "voivodeship", "county",
  "lead_type", "client_type", "sort", "dir", "favorites",
]
const PAGE_SIZE = 25
const EXCL_DESC = "Kontakt tylko dla Ciebie"
const EXCL_SOLD = "Niedostępna — sprzedano już slot współdzielony"
const EXCL_HELD = "Niedostępna — slot współdzielony jest zarezerwowany"

// A label/value pair as the handoff CSS expects it: .ll-facts > .ll-fact / .ll-fields > div, dt above dd.
function fieldPair(field, className = "") {
  const wrap = document.createElement("div")
  if (className) wrap.className = className
  const dt = document.createElement("dt"); dt.textContent = field.title
  const dd = document.createElement("dd"); dd.textContent = field.value; dd.title = field.value
  wrap.append(dt, dd)
  return wrap
}

export default class extends Controller {
  static targets = [
    "list", "row", "checkAll", "bulk", "bulkCount", "bulkSum",
    "viewBtn",
    "title", "count",
  ]
  static values = {
    url:               { type: String, default: "/api/catalog/" },
    vertical:          String,
    cartAddUrl:        String,
    cartRemoveUrl:     String,
    cartBulkUrl:       String,
    purchasingEnabled: { type: Boolean, default: true },
  }

  connect() {
    this._params = new URLSearchParams(window.location.search)
    this._bulkV = "ex"

    // Restore view from localStorage (may throw in private / blocked storage).
    let saved = null
    try { saved = localStorage.getItem("ll-view") } catch { /* */ }
    this._view = saved || "extended"

    // Close variant menus when clicking outside and on Escape.
    this._onDoc = (e) => {
      this.element.querySelectorAll(".ll-add-menu:not([hidden])").forEach(m => {
        if (!m.parentElement.contains(e.target)) m.hidden = true
      })
    }
    this._onKey = (e) => {
      if (e.key === "Escape") {
        this.element.querySelectorAll(".ll-add-menu").forEach(m => { m.hidden = true })
      }
    }
    document.addEventListener("click", this._onDoc)
    document.addEventListener("keydown", this._onKey)
    this._onCartRemoved = (event) => {
      if (!this.element.isConnected) return
      const { lead_id, subcategory_id } = event.detail || {}
      for (const row of this.rowTargets) {
        if (row.dataset.lead === String(lead_id) && row.dataset.subcategory === String(subcategory_id)) {
          this._unsetInCart(row)
          this._refreshRow(row)
        }
      }
    }
    document.addEventListener("cart:removed", this._onCartRemoved)

    this._load()
  }

  disconnect() {
    document.removeEventListener("click", this._onDoc)
    document.removeEventListener("keydown", this._onKey)
    document.removeEventListener("cart:removed", this._onCartRemoved)
    clearTimeout(this._noticeTimeout)
  }

  // ---- view ----------------------------------------------------------------

  setView(e) {
    const v = e.params.view
    this._view = v
    try { localStorage.setItem("ll-view", v) } catch { /* */ }
    this._applyView()
  }

  _applyView() {
    const compact = this._view === "compact"
    if (this.hasListTarget) this.listTarget.classList.toggle("is-compact", compact)
    this.viewBtnTargets.forEach(b => {
      b.setAttribute("aria-pressed", String(b.dataset.view === this._view))
    })
    if (compact) this.rowTargets.forEach(r => this._setOpen(r, false))
  }

  get _isCompact() {
    return this._view === "compact" && matchMedia("(min-width: 1181px)").matches
  }

  // ---- row interactions ----------------------------------------------------

  rowClick(e) {
    if (e.target.closest("button, a, input, label, .ll-drawer, .ll-menu")) return
    const row = e.currentTarget
    if (row.classList.contains("is-in-cart")) return
    if (this._isCompact) this._toggleSelect(row)
    else this._setOpen(row, !row.classList.contains("is-open"))
  }

  toggleMore(e) {
    const r = e.currentTarget.closest(".ll-row")
    if (r) this._setOpen(r, !r.classList.contains("is-open"))
  }

  _setOpen(row, open) {
    const d = row.querySelector(".ll-drawer")
    if (!d) return
    d.hidden = !open
    row.classList.toggle("is-open", open)
    row.querySelector(".ll-more")?.setAttribute("aria-expanded", String(open))
    // Compact view: the eye button drives the same accordion.
    const peek = row.querySelector(".ll-peek")
    if (peek) {
      peek.setAttribute("aria-expanded", String(open))
      peek.classList.toggle("is-active", open)
    }
  }

  // ---- selection -----------------------------------------------------------

  get _selectable() {
    return this.rowTargets.filter(r => !r.hidden && !r.classList.contains("is-in-cart"))
  }
  get _selected() {
    return this.rowTargets.filter(r => r.querySelector(".ll-check")?.checked)
  }

  toggleCheck(e) {
    e.currentTarget.closest(".ll-row")?.classList.toggle("is-selected", e.target.checked)
    this._syncSel()
  }

  _toggleSelect(row) {
    const cb = row.querySelector(".ll-check")
    if (!cb || cb.disabled) return
    cb.checked = !cb.checked
    row.classList.toggle("is-selected", cb.checked)
    this._syncSel()
  }

  _setChecked(row, on) {
    const cb = row.querySelector(".ll-check")
    if (cb) cb.checked = on
    row.classList.toggle("is-selected", on)
  }

  toggleAll(e) {
    this._selectable.forEach(r => this._setChecked(r, e.target.checked))
    this._syncSel()
  }

  clearSelection() {
    this.rowTargets.forEach(r => this._setChecked(r, false))
    this._syncSel()
  }

  bulkVariant(e) {
    this._bulkV = e.target.value
    this._syncSel()
  }

  _syncSel() {
    const sel = this._selected
    const all = this._selectable.length
    if (this.hasBulkTarget) this.bulkTarget.hidden = sel.length === 0
    if (this.hasBulkCountTarget) this.bulkCountTarget.textContent = sel.length
    if (this.hasBulkSumTarget) {
      const total = sel.reduce((s, r) => s + this._priceOf(r, this._variantFor(r, this._bulkV)), 0)
      this.bulkSumTarget.textContent = this._fmt(total)
    }
    if (this.hasCheckAllTarget) {
      this.checkAllTarget.checked = sel.length > 0 && sel.length === all
      this.checkAllTarget.indeterminate = sel.length > 0 && sel.length < all
    }
  }

  async bulkAdd() {
    const sel = this._selected
    if (!sel.length || this._bulkAdding) return
    const variant = this._bulkV
    this._bulkAdding = true
    try {
      for (const row of sel) await this._addToCart(row, variant, true)
    } finally {
      this._bulkAdding = false
      this._syncSel()
    }
  }

  // ---- cart ----------------------------------------------------------------

  toggleAddMenu(e) {
    const menu = e.currentTarget.parentElement.querySelector(".ll-add-menu")
    if (!menu) return
    const willOpen = menu.hidden
    this.element.querySelectorAll(".ll-add-menu").forEach(m => { m.hidden = true })
    menu.hidden = !willOpen
  }

  addDefault(e) {
    const row = e.currentTarget.closest(".ll-row")
    this._addToCart(row, this._defaultVariant(row))
  }

  addVariant(e) {
    this._addToCart(e.currentTarget.closest(".ll-row"), e.params.variant)
  }

  chooseVariant(e) {
    const row = e.target.closest(".ll-row")
    if (!row) return
    this._syncVariant(row, e.target.value)
  }

  addChosen(e) {
    const r = e.currentTarget.closest(".ll-row")
    if (r) this._addToCart(r, r.dataset.variant || this._defaultVariant(r))
  }

  removeFromCart(e) {
    const row = e.currentTarget.closest(".ll-row")
    if (row) this._removeFromCart(row)
  }

  async _addToCart(row, variant, rowErrors = false) {
    if (!row || row.classList.contains("is-in-cart")) return
    const report = (message) => rowErrors ? this._showRowError(row, message) : this._flashError(message)
    if (row.dataset.exclOk === "0" && row.dataset.slotOk === "0") {
      if (rowErrors) report("Ten lead nie jest już dostępny.")
      return
    }
    if (!this.purchasingEnabledValue) {
      report("Konto nie zostało zweryfikowane — skontaktuj się z nami, aby aktywować zakupy.")
      return
    }
    const mode = variant === "slot" ? "slot" : "exclusivity"
    const res = await sendJSON("POST", this.cartAddUrlValue, {
      lead: Number(row.dataset.lead),
      subcategory: Number(row.dataset.subcategory),
      mode,
    })
    if (res.ok) {
      row.querySelector('[data-cart-error]')?.remove()
      this._setInCart(row, variant)
      this._refreshCart()
      await this._refreshRow(row)
    } else if (res.status === 403) {
      report(errorDetail(res.data, "Brak dostępu do zakupów."))
    } else {
      report(errorDetail(res.data, "Nie udało się dodać do koszyka."))
    }
  }

  async _removeFromCart(row) {
    const lead = Number(row.dataset.lead)
    const subcategory = Number(row.dataset.subcategory)
    if (!lead || !subcategory) return
    const res = await sendJSON("POST", this.cartRemoveUrlValue, { lead, subcategory })
    if (res.ok) {
      this._unsetInCart(row)
      this._refreshCart()
      await this._refreshRow(row)
    } else {
      this._flashError(errorDetail(res.data, "Nie udało się usunąć z koszyka."))
    }
  }

  _setInCart(row, variant) {
    row.classList.add("is-in-cart")
    row.dataset.cart = variant
    const tag = variant === "slot" ? "SLOT" : "WYŁ."
    row.querySelectorAll('[data-slot="cartTag"]').forEach(t => { t.textContent = tag })
    row.querySelector(".ll-add-menu")?.setAttribute("hidden", "")
    // The accordion's variant card shows the variant that is in the cart.
    this._syncVariant(row, variant)
    this._setChecked(row, false)
    const cb = row.querySelector(".ll-check"); if (cb) cb.disabled = true
    this.dispatch("cart", { detail: { lead_id: row.dataset.id, variant, action: "add" } })
    this._syncSel()
  }

  _unsetInCart(row) {
    row.classList.remove("is-in-cart")
    delete row.dataset.cart
    const cb = row.querySelector(".ll-check"); if (cb) cb.disabled = false
    this.dispatch("cart", { detail: { lead_id: row.dataset.id, variant: null, action: "remove" } })
    this._syncSel()
  }

  // Ask the cart controller (scoped to <body>) to refresh its count.
  // cart_controller.js exposes a public load() that GETs /api/cart/ and updates countValue.
  _refreshCart() {
    try {
      const host = document.querySelector('[data-controller~="cart"]')
      if (host) this.application.getControllerForElementAndIdentifier(host, "cart")?.load()
    } catch { /* cart controller absent in test/staff context */ }
  }

  // ---- loading + rendering -------------------------------------------------

  async _load() {
    this._showSkeleton(true)
    const query = new URLSearchParams()
    // Forward standard filter keys and repeated values (e.g. multiple county entries).
    for (const key of [...FILTER_KEYS, "page"]) {
      for (const v of this._params.getAll(key)) query.append(key, v)
    }
    // Forward dynamic f_* and schema sf_* filter params (repeated for multi-select; sf_<key>__min/__max for ranges).
    for (const key of [...new Set(this._params.keys())].filter(k => k.startsWith("f_") || k.startsWith("sf_"))) {
      for (const v of this._params.getAll(key)) query.append(key, v)
    }
    if (this.verticalValue) query.set("vertical", this.verticalValue)
    let data
    try {
      const qs = query.toString()
      data = await getJSON(qs ? `${this.urlValue}?${qs}` : this.urlValue)
    } catch {
      this._showSkeleton(false)
      this._flashError("Nie udało się załadować leadów. Odśwież stronę.")
      return
    }
    this._showSkeleton(false)
    const items = listResults(data)
    if (this.hasCountTarget) this.countTarget.textContent = data.count ?? "0"
    this._renderRows(items)
    this._renderShown(items.length, data.count)
    this._renderPager(data)
  }

  _showSkeleton(on) {
    const sk = this.element.querySelector("#ll-skeleton")
    if (!sk) return
    sk.hidden = !on
    sk.setAttribute("aria-busy", String(on))
  }

  _renderRows(items) {
    const container = this.element.querySelector("#ll-rows")
    const emptyEl  = this.element.querySelector("#ll-empty")
    const thead    = this.element.querySelector("#ll-thead")
    const pager    = this.element.querySelector("#ll-pager")
    if (!container) return

    container.textContent = ""

    if (!items.length) {
      if (emptyEl) emptyEl.hidden = false
      if (thead) thead.hidden = true
      if (pager) pager.hidden = true
      return
    }

    if (emptyEl) emptyEl.hidden = true
    if (thead) thead.hidden = false
    if (pager) pager.hidden = false

    const template = document.getElementById("tpl-ll-row")
    if (!template) return

    for (const item of items) {
      const row = template.content.firstElementChild.cloneNode(true)
      this._fillRow(row, item)
      container.appendChild(row)
    }

    this._applyView()
    this._syncSel()
  }

  _fillRow(row, item) {
    // Root data attributes (used by selection + cart helpers).
    row.dataset.id          = item.id
    row.dataset.listingId   = item.id
    row.dataset.lead        = item.lead_id
    row.dataset.subcategory = item.subcategory_id
    row.dataset.price       = item.price_pln ?? ""
    row.dataset.slotPrice   = item.slot_price_pln ?? ""
    row.dataset.free        = item.slots_remaining ?? "0"

    // Title & code.
    const titleEl = row.querySelector('[data-slot="title"]')
    if (titleEl) titleEl.textContent = item.category_path || ""
    const codeEl = row.querySelector('[data-slot="code"]')
    if (codeEl) codeEl.textContent = item.code || ""

    // Potential badge — modifier class by score (no "age" slot; brief: pot replaces it).
    const potEl = row.querySelector('[data-slot="pot"]')
    if (potEl) {
      potEl.textContent = item.score_label || ""
      potEl.className = "ll-pot"
      if (item.score === "hot")  potEl.classList.add("ll-pot--hot")
      else if (item.score === "warm") potEl.classList.add("ll-pot--warm")
      else if (item.score === "cold") potEl.classList.add("ll-pot--cold")
    }

    // Facts: first 3 fields with list:true — label (dt) above value (dd).
    const factsEl = row.querySelector('[data-slot="facts"]')
    if (factsEl) {
      factsEl.textContent = ""
      const listFields = (item.shop_fields || []).filter(f => f.list).slice(0, 3)
      for (const f of listFields) factsEl.append(fieldPair(f, "ll-fact"))
      // No facts → hide the block (on cards it would be two divider lines around blank space).
      factsEl.hidden = listFields.length === 0
    }

    // Region — split into voivodeship / county on " / " separator.
    const parts = (item.region || "").split(" / ").map(s => s.trim())
    const wojEl = row.querySelector('[data-slot="woj"]')
    if (wojEl) wojEl.textContent = parts[0] || item.region || ""
    const powEl = row.querySelector('[data-slot="pow"]')
    if (powEl) powEl.textContent = parts[1] || ""

    // Exclusivity price — fills all [data-slot="price"] (main price + menu + variant card).
    const exclText = formatPln(item.price_pln)
    row.querySelectorAll('[data-slot="price"]').forEach(el => { el.textContent = exclText })

    // Slot price — fills all [data-slot="slotPrice"].
    const slotPriceText = item.slot_price_pln != null ? formatPln(item.slot_price_pln) : "—"
    row.querySelectorAll('[data-slot="slotPrice"]').forEach(el => { el.textContent = slotPriceText })

    // Slot dots, free text, caret and slot option — also re-run after cart changes (_refreshRow).
    this._fillSlots(row, item)

    // Accordion: all shop_fields except note — filled in the drawer dl.
    const allEl = row.querySelector('[data-slot="all"]')
    if (allEl) {
      allEl.textContent = ""
      for (const f of (item.shop_fields || []).filter(f => f.key !== "note")) allEl.append(fieldPair(f))
    }

    // Note card — show only when present.
    const noteEl = row.querySelector('[data-slot="note"]')
    if (noteEl) {
      const noteField = (item.shop_fields || []).find(f => f.key === "note")
      noteEl.textContent = noteField?.value || ""
      const noteCard = noteEl.closest(".ll-note")
      if (noteCard) noteCard.hidden = !noteField?.value
    }

    // The two variant radios are one group per row (the row lives in a <template>: the name is set per clone).
    row.querySelectorAll('.ll-variant input[type="radio"]').forEach(r => { r.name = `variant-${item.id}` })

    // Chosen price initial value (exclusivity is the default radio).
    const chosenPriceEl = row.querySelector('[data-slot="chosenPrice"]')
    if (chosenPriceEl) chosenPriceEl.textContent = formatPln(item.price_pln)
    // Default variant = the first purchasable one (the shared slot when exclusivity is blocked).
    if (!item.in_cart) this._syncVariant(row, this._defaultVariant(row))

    // In-cart state from API (in_cart_mode: "exclusive" | "slot" | null).
    if (item.in_cart) {
      const mode = item.in_cart_mode === "slot" ? "slot" : "ex"
      row.classList.add("is-in-cart")
      row.dataset.cart = mode
      const tag = mode === "slot" ? "SLOT" : "WYŁ."
      row.querySelectorAll('[data-slot="cartTag"]').forEach(t => { t.textContent = tag })
      const cb = row.querySelector(".ll-check"); if (cb) cb.disabled = true
      this._syncVariant(row, mode)
    }
  }

  // Slot-related parts of a row only (dots, „wolne: N", slot caret/option availability, dataset.free).
  // Idempotent in both directions: _fillRow calls it on a fresh clone, _refreshRow on a live row.
  // Leaves the accordion, the chosen variant and the in-cart state alone.
  _fillSlots(row, item) {
    const remaining = item.slots_remaining ?? 0
    row.dataset.free = item.slots_remaining ?? "0"

    // Slot dots C21.1: filled = taken, outlined = free.
    const slotsEl = row.querySelector('[data-slot="slots"]')
    if (slotsEl) {
      slotsEl.textContent = ""
      const capacity = item.slot_capacity || 0
      // Taken = paid + reserved (what „wolne: N” already excludes); slots_sold counts paid only.
      const sold     = Math.max(0, capacity - (item.slots_remaining ?? capacity))
      for (let i = 0; i < capacity; i++) {
        // Handoff C21.1: <i> = taken, <i class="is-free"> = free.
        const dot = document.createElement("i")
        if (i >= sold) dot.className = "is-free"
        slotsEl.appendChild(dot)
      }
    }

    // Free slots text + strike "Slot" at 0 free.
    const freeEl = row.querySelector('[data-slot="free"]')
    const none = Boolean(item.slot_available) && remaining === 0
    if (freeEl) {
      if (item.slot_available && remaining > 0) freeEl.textContent = `wolne: ${remaining}`
      else if (none) freeEl.textContent = "brak wolnych"
      else freeEl.textContent = ""
    }
    // Handoff: „Slot” struck through when no slot is free (`.ll-slot.is-none .ll-slot-txt`).
    row.querySelector(".ll-slot")?.classList.toggle("is-none", none)

    // BUR: no caret when slot_available is false.
    const caret = row.querySelector(".ll-add-caret")
    if (caret) caret.hidden = !item.slot_available
    const slotBtn = row.querySelector('[data-slot="slotBtn"]')
    if (slotBtn) {
      slotBtn.hidden = !item.slot_available
      slotBtn.disabled = Boolean(item.slot_available) && remaining === 0
    }

    // Slot option in the variant picker drawer.
    const slotOpt = row.querySelector('[data-slot="slotOpt"]')
    if (slotOpt) {
      slotOpt.hidden = !item.slot_available
      if (item.slot_available) {
        const descText = remaining > 0
          ? `Slot współdzielony · wolne: ${remaining}`
          : "Brak wolnych slotów"
        row.querySelectorAll('[data-slot="slotDesc"]').forEach(el => { el.textContent = descText })
      }
    }

    this._applyAvailability(row, item, remaining)
  }

  // Mirrors the server rules (assert_exclusivity_not_blocked_by_slots, assert_slot_available_for_reservation)
  // through the serializer's `exclusivity_available`, `slot_available` and `slots_remaining`: an option the server
  // would refuse is DISABLED (accordion radio + split-button menu item) with a one-line reason, never hidden.
  _applyAvailability(row, item, remaining) {
    const exclOk = item.exclusivity_available !== false
    const slotOk = Boolean(item.slot_available) && remaining > 0
    row.dataset.exclOk = exclOk ? "1" : "0"
    row.dataset.slotOk = slotOk ? "1" : "0"

    const reason = Number(item.slots_sold) > 0 ? EXCL_SOLD : EXCL_HELD
    row.querySelectorAll('[data-slot="exclDesc"]').forEach(el => { el.textContent = exclOk ? EXCL_DESC : reason })
    const exclRadio = row.querySelector('.ll-variant input[value="ex"]')
    if (exclRadio) exclRadio.disabled = !exclOk
    row.querySelector('[data-slot="exclOpt"]')?.setAttribute("aria-disabled", String(!exclOk))
    const exclBtn = row.querySelector('[data-slot="exclBtn"]')
    if (exclBtn) exclBtn.disabled = !exclOk
    const slotRadio = row.querySelector('.ll-variant input[value="slot"]')
    if (slotRadio) slotRadio.disabled = !slotOk

    // Nothing purchasable: the add buttons are disabled as well.
    const nothing = !exclOk && !slotOk
    row.querySelectorAll(".ll-add-main, .ll-add-caret, .ll-variant-add").forEach(b => { b.disabled = nothing })

    // A refresh may have blocked the chosen variant: move the card to the first purchasable one.
    if (row.dataset.variant && !row.classList.contains("is-in-cart")) this._syncVariant(row, row.dataset.variant)
  }

  // Re-read one listing from the catalog retrieve endpoint and refresh its slot data in place.
  // Never throws and never shows an error: a failed refresh leaves the row as it is.
  async _refreshRow(row) {
    const id = Number(row?.dataset.listingId)
    if (!id) return
    const base = this.urlValue.endsWith("/") ? this.urlValue : `${this.urlValue}/`
    const query = new URLSearchParams()
    if (this.verticalValue) query.set("vertical", this.verticalValue)
    const qs = query.toString()
    try {
      const item = await getJSON(qs ? `${base}${id}/?${qs}` : `${base}${id}/`)
      if (!row.isConnected) return
      this._fillSlots(row, item)
      this._syncSel()
    } catch { /* keep the row as it is */ }
  }

  _renderShown(count, total) {
    const bar = this.element.querySelector("#ll-shown")
    if (!bar) return
    const page = (Number.parseInt(this._params.get("page"), 10) || 1)
    const start = (page - 1) * PAGE_SIZE + 1
    const end   = start + count - 1
    bar.textContent = ""
    const prefix = document.createTextNode("Pokazano ")
    const bRange = document.createElement("b")
    bRange.textContent = count === 0 ? "0" : `${start}–${end}`
    const mid = document.createTextNode(" z ")
    const bTotal = document.createElement("b")
    bTotal.textContent = total ?? "0"
    bar.append(prefix, bRange, mid, bTotal)
  }

  _renderPager(data) {
    const pagerPages = this.element.querySelector("#ll-pager .ll-pager-pages")
    if (!pagerPages) return
    pagerPages.textContent = ""
    if (!data.count || data.count <= PAGE_SIZE) return

    const page       = (Number.parseInt(this._params.get("page"), 10) || 1)
    const totalPages = Math.ceil(data.count / PAGE_SIZE)

    const makeLink = (label, targetPage, enabled, active) => {
      const el = (active || !enabled) ? document.createElement("span") : document.createElement("a")
      el.className = ["ll-page", active && "is-active", !enabled && "is-disabled"].filter(Boolean).join(" ")
      el.textContent = label
      if (enabled && !active) el.href = this._pageHref(targetPage)
      pagerPages.appendChild(el)
    }

    makeLink("←", page - 1, Boolean(data.previous), false)

    let lastRendered = 0
    for (let p = 1; p <= totalPages; p++) {
      const near = Math.abs(p - page) <= 1 || p === 1 || p === totalPages
      if (near) {
        if (lastRendered && p - lastRendered > 1) {
          const dots = document.createElement("span")
          dots.className = "ll-page ll-page--dots"
          dots.textContent = "…"
          pagerPages.appendChild(dots)
        }
        makeLink(String(p), p, p !== page, p === page)
        lastRendered = p
      }
    }

    makeLink("→", page + 1, Boolean(data.next), false)
  }

  _pageHref(page) {
    const query = new URLSearchParams()
    for (const key of FILTER_KEYS) {
      for (const v of this._params.getAll(key)) query.append(key, v)
    }
    for (const key of [...new Set(this._params.keys())].filter(k => k.startsWith("f_") || k.startsWith("sf_"))) {
      for (const v of this._params.getAll(key)) query.append(key, v)
    }
    // The vertical is in the page path (/sklep/<vertical>/) — only the API query needs it.
    if (page > 1) query.set("page", String(page))
    const qs = query.toString()
    return qs ? `?${qs}` : window.location.pathname
  }

  // ---- helpers -------------------------------------------------------------

  // One source of truth for the accordion's variant card: exactly one radio checked (the browser enforces
  // it through the shared name; the explicit loop covers radios without a group), row.dataset.variant and
  // the „Dodaj do koszyka · <price>" label follow it. A hidden slot option leaves exclusivity as the only choice.
  _syncVariant(row, wanted) {
    const slotOpt = row.querySelector('[data-slot="slotOpt"]')
    let variant = wanted === "slot" && !slotOpt?.hidden ? "slot" : "ex"
    // Exclusivity the server would refuse never stays selected while the shared slot can be bought.
    if (variant === "ex" && row.dataset.exclOk === "0" && row.dataset.slotOk === "1") variant = "slot"
    row.querySelectorAll('.ll-variant input[type="radio"]').forEach(r => { r.checked = r.value === variant })
    row.dataset.variant = variant
    const out = row.querySelector('[data-slot="chosenPrice"]')
    if (out) out.textContent = this._fmt(this._priceOf(row, variant))
  }

  _defaultVariant(row) {
    return row.dataset.exclOk === "0" && row.dataset.slotOk === "1" ? "slot" : "ex"
  }

  _variantFor(row, wanted) {
    return wanted === "slot" && Number(row.dataset.free) > 0 ? "slot" : "ex"
  }

  _priceOf(row, v) {
    return Number(v === "slot" ? row.dataset.slotPrice : row.dataset.price) || 0
  }

  _fmt(n) { return formatPln(n) }

  _showRowError(row, message) {
    let notice = row.querySelector('[data-cart-error]')
    if (!notice) {
      notice = document.createElement('p')
      notice.className = 'll-notice'
      notice.setAttribute('data-cart-error', '')
      notice.setAttribute('role', 'alert')
      row.append(notice)
    }
    notice.textContent = message
  }

  _flashError(msg) {
    let notice = this.element.querySelector(":scope > .ll-notice")
    if (!notice) {
      notice = document.createElement("div")
      notice.className = "ll-notice"
      this.element.prepend(notice)
    }
    notice.textContent = msg
    notice.hidden = false
    clearTimeout(this._noticeTimeout)
    this._noticeTimeout = setTimeout(() => { if (notice) notice.hidden = true }, 6000)
  }
}
